import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDocument } from "@/features/document/hooks";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye } from "lucide-react";
import { FilePreviewModal } from "@/components/ui/FilePreviewModal";

export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const numId = Number(id);
  const [previewFile, setPreviewFile] = useState<{
    fileId: number;
    fileName: string | null;
    source?: "files" | "attach";
  } | null>(null);
  const { data: doc, isLoading } = useDocument(Number.isFinite(numId) ? numId : undefined);

  if (isLoading) return <div className="text-muted-foreground">Đang tải...</div>;
  if (!doc) return <div className="text-muted-foreground">Không tìm thấy văn bản.</div>;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Button size="sm" variant="ghost" onClick={() => nav(-1)}>
          <ArrowLeft className="h-4 w-4" /> Quay lại
        </Button>
        <h1 className="text-xl font-semibold">{doc.title}</h1>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
        <div>
          <span className="text-muted-foreground">Số ký hiệu:</span> {doc.registerNumber ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Mã văn bản:</span> {doc.code ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Người ký:</span> {doc.signer ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Ngày ban hành:</span> {doc.promulgateDate ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Ngày nhận:</span> {doc.receiveDate ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Hạn xử lý:</span> {doc.deadlineDate ?? "—"}
        </div>
      </div>

      <section className="mb-4">
        <h2 className="font-semibold mb-2">Nội dung</h2>
        <div className="text-sm whitespace-pre-wrap rounded-md border border-border p-3 bg-muted/30">
          {doc.content ?? "—"}
        </div>
      </section>

      <section className="mb-4">
        <h2 className="font-semibold mb-2">Đính kèm ({doc.attachments.length})</h2>
        <ul className="space-y-1">
          {doc.attachments.map((a) => (
            <li key={a.fileId} className="flex items-center gap-2 text-sm">
              <span>{a.fileName ?? "(unnamed)"}</span>
              <button
                type="button"
                className="text-primary-600 hover:underline inline-flex items-center gap-1"
                onClick={() =>
                  setPreviewFile({
                    fileId: a.fileId,
                    fileName: a.fileName ?? null,
                    source: a.source ?? "attach",
                  })
                }
              >
                <Eye className="h-3 w-3" /> Xem
              </button>
            </li>
          ))}
          {doc.attachments.length === 0 && (
            <li className="text-muted-foreground text-sm">Chưa có tệp</li>
          )}
        </ul>
      </section>

      <FilePreviewModal
        open={previewFile !== null}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />

      <section>
        <h2 className="font-semibold mb-2">Người nhận ({doc.receivers.length})</h2>
        <ul className="text-sm">
          {doc.receivers.map((r) => (
            <li key={r.staffId}>
              {r.fullName ?? r.loginName ?? r.staffId} {r.orgName ? `— ${r.orgName}` : ""}
            </li>
          ))}
          {doc.receivers.length === 0 && (
            <li className="text-muted-foreground">Chưa có người nhận</li>
          )}
        </ul>
      </section>
    </div>
  );
}
